import React, { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { useI18n } from "@/i18n";
import api from "@/lib/api";

const LEAD_ROWS = [
  ["first_name", "Prénom"], ["last_name", "Nom"], ["email", "Courriel"], ["phone", "Téléphone"],
  ["city", "Ville"], ["province", "Province"], ["call_reason", "Raison du contact"],
  ["candidate_status", "Statut"], ["interests", "Intérêts"],
  ["experience_summary", "Expérience"], ["opportunity_preferences", "Préférences"],
];

function value(v) {
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (v === true) return "Oui";
  if (v === false) return "Non";
  return v || "—";
}

export default function AdminLiveChat() {
  const { t } = useI18n();
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/livechat/sessions")
      .then((res) => setSessions(res.data.sessions))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setDetail(null);
    api.get(`/admin/livechat/sessions/${selected}`).then((res) => setDetail(res.data));
  }, [selected]);

  return (
    <div data-testid="admin-livechat" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-light">{t("admin.livechat")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Conversations du clavardage public, en lecture seule. Prêtes pour VytaliLeads (non synchronisées).
        </p>
      </div>

      {loading && <p className="text-sm text-muted-foreground">…</p>}

      {!loading && sessions.length === 0 && (
        <p data-testid="admin-livechat-empty" className="border border-border bg-surface p-8 text-sm text-muted-foreground">
          Aucune conversation pour le moment.
        </p>
      )}

      {sessions.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <ul data-testid="admin-livechat-list" className="divide-y divide-border border border-border bg-surface">
              {sessions.map((s) => (
                <li key={s.session_id}>
                  <button
                    type="button"
                    data-testid={`admin-livechat-row-${s.session_id}`}
                    onClick={() => setSelected(s.session_id)}
                    className={`flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-muted ${
                      selected === s.session_id ? "bg-secondary" : ""
                    }`}
                  >
                    <span className="flex items-center gap-2 text-sm">
                      <MessageCircle className="h-3.5 w-3.5 text-accent" />
                      {s.lead?.first_name || "Visiteur"} {s.lead?.last_name || ""}
                      <span className="ml-auto text-xs uppercase tracking-wider text-muted-foreground">
                        {s.language}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(s.created_at).toLocaleString()} · {s.message_count} message(s) · {s.status}
                    </span>
                    {s.human_follow_up_needed && (
                      <span className="text-xs text-accent">Suivi humain demandé</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            {!selected && (
              <p className="border border-border bg-surface p-8 text-sm text-muted-foreground">
                Sélectionnez une conversation.
              </p>
            )}
            {selected && !detail && <p className="text-sm text-muted-foreground">…</p>}
            {detail && (
              <div data-testid="admin-livechat-detail" className="space-y-6">
                <div className="border border-border bg-surface p-5">
                  <p className="cs-kicker text-muted-foreground">Coordonnées recueillies</p>
                  <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    {LEAD_ROWS.map(([key, label]) => (
                      <div key={key} className="text-sm">
                        <dt className="text-xs text-muted-foreground">{label}</dt>
                        <dd>{value(detail.lead?.[key])}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="mt-4 text-xs text-muted-foreground">
                    Résumé : {detail.lead?.conversation_summary || "—"}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    VytaliLeads : {detail.vytali_leads?.status} · source : {detail.source}
                  </p>
                </div>

                <div className="space-y-3 border border-border bg-surface p-5">
                  <p className="cs-kicker text-muted-foreground">Transcription</p>
                  {detail.messages?.map((m, index) => (
                    <div
                      key={index}
                      className={`max-w-[85%] whitespace-pre-wrap px-3 py-2 text-sm ${
                        m.role === "user" ? "ml-auto bg-accent text-accent-foreground" : "bg-secondary"
                      }`}
                    >
                      {m.content}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
